import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './scss/ui.scss';
import './scss/_fonts.scss';
import './scss/App.scss';
import { useState, useEffect } from 'react';
import logo from './images/logo-long.svg';
import { authService } from './services/authService';
import api from './services/apiService';
import PaymentSuccess from './PaymentSuccess';
import { pushNotificationService } from './services/pushNotificationService';
import { applyIdentityFromLoginResult, clearSessionState } from './utils/sessionReset';
import PaymentReturn from './components/PaymentReturn';
import { Routes, Route } from 'react-router-dom';
// import clarityService from './services/clarityService'; // Disabled - tracking removed for App Store compliance
import ProfileSelection from './components/ProfileSelection';
import { biometricService } from './services/biometricService';
import BiometricPromptModal from './components/BiometricPromptModal';
import PermissionsPromptModal from './components/PermissionsPromptModal';
import {
  clearPushReminderState,
  getHowToEnableSteps,
  markPushPromptShownThisSession,
  markPushPromptSkipped,
  shouldShowPeriodicPushModal
} from './services/pushPermissionReminders';
import NewRegisterModal from './components/NewRegisterModal';
import { safeAreaUtils } from './utils/safeArea';
import { Capacitor } from '@capacitor/core';
import { FaChevronRight } from 'react-icons/fa';
import { canOpenAcademySettings } from './utils/academyAccess';

// Check if running in Capacitor
const isCapacitor = window.Capacitor?.isNativePlatform() || false;

function handlePushNotificationAction(notification, navigate) {
  const data = notification.notification?.data || {};
  if (data.type === 'payment' || data.type === 'treasurer_notice') {
    navigate('/membership');
    return;
  }
  if (data.type === 'product_news' || data.news_id) {
    if (data.url && /^https?:\/\//i.test(data.url)) {
      window.open(data.url, '_blank', 'noopener,noreferrer');
    } else {
      navigate('/notifications');
    }
    return;
  }
  if (data.type === 'parent_approval_needed') {
    if (data.team_id) {
      sessionStorage.setItem('selected_team_id', String(data.team_id));
    }
    navigate('/players?filter=pending');
    return;
  }
  if (data.match_id) {
    navigate(`/match/${data.match_id}`);
  }
}

// Initialize safe area handling for Android (runs immediately)
safeAreaUtils.init();

// Enable debug mode in development
const isDevelopment = process.env.NODE_ENV === 'development';

// DEBUG: Set safe area values for testing (set in browser console)
// Example: window.DEBUG_SAFE_AREA_TOP = 50; window.DEBUG_SAFE_AREA_BOTTOM = 0;
// Then refresh the page
if (isDevelopment && window.DEBUG_SAFE_AREA_TOP === undefined) {
  // Set default debug value for testing
  window.DEBUG_SAFE_AREA_TOP = 50;
  window.DEBUG_SAFE_AREA_BOTTOM = 0;
  console.log('[DEBUG] Safe area debug mode enabled. Set window.DEBUG_SAFE_AREA_TOP and window.DEBUG_SAFE_AREA_BOTTOM to override.');
}

// Only show debugger in Capacitor or when explicitly enabled for development
const shouldShowDebugger = isCapacitor || 
                          (process.env.NODE_ENV === 'development' && process.env.REACT_APP_ENABLE_DEBUGGER === 'true');

// Expose biometric clear function for testing (development only)
if (isDevelopment || isCapacitor) {
  window.clearBiometric = async () => {
    try {
      const { authService } = await import('./services/authService');
      await authService.disableBiometricLogin();
      console.log('✅ Biometric credentials cleared! You can now test the setup process again.');
      console.log('💡 Refresh the page to see the login form.');
      return true;
    } catch (error) {
      console.error('❌ Error clearing biometric:', error);
      return false;
    }
  };
  console.log('💡 Development helper: Run clearBiometric() in console to clear Face ID credentials for testing');
}

function App() {
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showDebugger, setShowDebugger] = useState(false);
  const [showProfileSelection, setShowProfileSelection] = useState(false);
  const [availableProfiles, setAvailableProfiles] = useState([]);
  const [loginAcademies, setLoginAcademies] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [showBiometricPrompt, setShowBiometricPrompt] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [canUseBiometric, setCanUseBiometric] = useState(false);
  const [showBiometricButton, setShowBiometricButton] = useState(false);
  const [showPermissionsPrompt, setShowPermissionsPrompt] = useState(false);
  const [needsNotificationPermission, setNeedsNotificationPermission] = useState(false);
  const [needsBiometricPermission, setNeedsBiometricPermission] = useState(false);
  const [notificationDenied, setNotificationDenied] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showLoginForm, setShowLoginForm] = useState(isCapacitor); // Show login form by default in app, app store buttons on web
  const navigate = useNavigate();
  const location = useLocation();

  // Get team ID from URL path, params, or localStorage (for iOS home screen compatibility)
  const pathTeamId = location.pathname !== '/' ? location.pathname.substring(1) : null;
  const searchParams = new URLSearchParams(location.search);
  const hashParams = location.hash ? new URLSearchParams(location.hash.substring(1)) : new URLSearchParams();
  const urlTeamId = pathTeamId || searchParams.get('team_id') || hashParams.get('team_id');
  const storedTeamId = localStorage.getItem('login_team_id');
  const teamId = urlTeamId || storedTeamId;
  
  // Store team ID in localStorage if provided via URL
  if (urlTeamId && urlTeamId !== storedTeamId) {
    localStorage.setItem('login_team_id', urlTeamId);
  }
  
  // Get team name from localStorage if available
  const teamName = localStorage.getItem('login_team_name');
  
  const teamLogo = teamId ? `https://football.pebbledesigns.co.uk/api/images/teams/${teamId}/logo-light.png` : null;
  const teamBackground = teamId ? `https://football.pebbledesigns.co.uk/api/images/teams/${teamId}/login_bg.jpg` : null;

  // Replace localStorage check with authService
  const isAdmin = authService.isAdmin();

  // Remove tab-specific body classes since we're using unified login
  useEffect(() => {
    document.body.classList.remove('tab-player', 'tab-coach', 'tab-parent');
  }, []);

  // Check for join parameter in URL to open registration modal
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const hash = location.hash.substring(1); // Remove the # symbol
    
    // Check for ?join or #join in URL
    if (searchParams.get('join') === 'true' || searchParams.has('join') || hash === 'join') {
      setShowRegisterModal(true);
    }
  }, [location.search, location.hash]);

  // Initialize push notifications for logged-in users
  useEffect(() => {
    // Clarity tracking disabled - removed for App Store compliance
    // setTimeout(() => {
    //   clarityService.init();
    //   
    //   // If user is already logged in, capture their data and initialize push notifications
    //   const userId = sessionStorage.getItem('user_id');
    //   if (userId) {
    //     clarityService.captureUserData();
    //     clarityService.setUserId(userId);
    
    // If user is already logged in, initialize push notifications
    setTimeout(() => {
      const userId = sessionStorage.getItem('user_id');
      if (userId && isCapacitor) {
          console.log('[App] User already logged in, initializing push notifications...');
          const initPushNotifications = async () => {
            try {
              console.log('[App] Setting up push notification listeners...');
              pushNotificationService.addListeners({
                onRegistration: (token) => {
                  console.log('[App] Push registration success callback, token:', token);
                },
                onRegistrationError: (error) => {
                  console.error('[App] Error on registration:', error);
                },
                onPushReceived: (notification) => {
                  console.log('[App] Push received:', notification);
                },
                onPushActionPerformed: (notification) => {
                  console.log('[App] Push action performed:', notification);
                  handlePushNotificationAction(notification, navigate);
                }
              });
              
              console.log('[App] Initializing push notifications for logged-in user...');
              await pushNotificationService.initialize();
            } catch (error) {
              console.error('[App] Error initializing push notifications:', error);
            }
          };
          
          initPushNotifications();
        }
    }, 100);
  }, [navigate, isCapacitor]);

  // Re-check notification permissions when app returns to foreground (login modal)
  useEffect(() => {
    if (!isCapacitor || !showPermissionsPrompt) {
      return;
    }

    const checkPermissionsOnResume = async () => {
      try {
        const { App } = await import('@capacitor/app');
        
        const listener = await App.addListener('appStateChange', async (state) => {
          if (!state.isActive) {
            return;
          }

          const recovered = await pushNotificationService.recoverIfGranted();
          if (recovered) {
            clearPushReminderState();
            setNotificationDenied(false);
            setNeedsNotificationPermission(false);
            if (!needsBiometricPermission) {
              setShowPermissionsPrompt(false);
              navigate('/team-selection');
            }
          }
        });

        return () => {
          listener.remove();
        };
      } catch (error) {
        console.error('[App] Error setting up app state listener:', error);
      }
    };

    checkPermissionsOnResume();
  }, [showPermissionsPrompt, needsBiometricPermission, navigate, isCapacitor]);

  useEffect(() => {
    document.body.classList.remove('root-page');
    
    if (location.pathname === '/' || location.pathname === '/login') {
      document.body.classList.add('root-page');
    }

    // Clarity tracking disabled - removed for App Store compliance
    // if (location.pathname !== '/academy-welcome') {
    //   clarityService.updateUserData();
    // }

    return () => {
      document.body.classList.remove('root-page');
    };
  }, [location.pathname]);

  // Add debug mode toggle with triple tap
  useEffect(() => {
    // Only add the triple tap listener if we're in Capacitor
    if (!shouldShowDebugger) return;
    
    let tapCount = 0;
    let lastTap = 0;
    
    const handleTap = () => {
      const now = new Date().getTime();
      const timeDiff = now - lastTap;
      
      if (timeDiff < 500 && timeDiff > 0) {
        tapCount++;
      } else {
        tapCount = 1;
      }
      
      lastTap = now;
      
      if (tapCount >= 3) {
        setShowDebugger(prev => !prev);
        tapCount = 0;
      }
    };
    
    document.addEventListener('click', handleTap);
    
    return () => {
      document.removeEventListener('click', handleTap);
    };
  }, []);

  // Handle biometric login (defined early so it can be used in useEffect)
  const handleBiometricLogin = async () => {
    try {
      setError('');
      
      // Get credentials from keychain using biometric
      const credentials = await biometricService.getCredentials();
      
      // Use the stored credentials to log in
      let formattedPhone = credentials.phone.replace(/[^0-9]/g, '');
      if (formattedPhone.startsWith('0')) {
        formattedPhone = formattedPhone.substring(1);
      }

      // Check for profiles
      const formData = new URLSearchParams();
      formData.append('phone', formattedPhone);
      formData.append('password', credentials.password);
      
      const profileResponse = await api.post('check_login_profiles.php', formData, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      });

      const profileResult = profileResponse.data;
      
      if (!profileResult.success) {
        setError('Biometric login failed. Please use phone number and password.');
        // Clear invalid credentials
        await authService.disableBiometricLogin();
        setShowBiometricButton(false);
        return;
      }

      // If single profile, proceed directly to login
      if (profileResult.single_profile) {
        await completeLogin(profileResult.profile, formattedPhone, credentials.password);
      } else {
        // Multiple profiles - store credentials in state for profile selection
        setPhone(credentials.phone); // Store original phone format
        setPassword(credentials.password);
        setAvailableProfiles(profileResult.profiles);
        setShowProfileSelection(true);
      }
    } catch (error) {
      console.error('Biometric login error:', error);
      // Handle user cancellation gracefully - just show the login form
      if (error.message && (error.message.includes('User cancelled') || error.message.includes('canceled') || error.message.includes('USER_CANCEL'))) {
        console.log('[App] User cancelled biometric login, showing login form');
        // Don't show error, just let them use phone/password
        setError('');
        return;
      }
      
      // For other errors, show message but don't clear credentials (might be temporary)
      setError('Biometric authentication failed. Please use phone number and password.');
      // Only clear credentials on actual authentication failures, not cancellations
      if (error.message && !error.message.includes('User cancelled') && !error.message.includes('canceled')) {
        // Don't auto-clear on first failure, let user try again
        console.log('[App] Biometric login failed, user can try again or use phone/password');
      }
    }
  };

  // Check biometric availability on mount and auto-login if credentials exist
  useEffect(() => {
    const checkBiometric = async () => {
      if (window.Capacitor?.isNativePlatform()) {
        try {
          // First, check if this is a fresh install with stale Keychain data
          // iOS preserves Keychain items even after app deletion
          const isFreshInstall = !localStorage.getItem('auth_token') && 
                                 !localStorage.getItem('biometric_enabled') &&
                                 !sessionStorage.getItem('user_id');
          
          if (isFreshInstall) {
            // Check if there are old credentials in Keychain from previous install
            try {
              const hasStaleCredentials = await biometricService.hasStoredCredentials();
              if (hasStaleCredentials) {
                console.log('[App] Fresh install detected with stale Keychain credentials - clearing them');
                await biometricService.deleteCredentials();
                console.log('[App] Stale biometric credentials cleared');
              }
            } catch (error) {
              // Ignore errors when checking for stale credentials
              console.log('[App] No stale credentials found or error checking:', error.message);
            }
          }

          const available = await biometricService.isAvailable();
          setBiometricAvailable(available);
          
          if (available) {
            try {
              const hasCredentials = await authService.canUseBiometricLogin();
              setCanUseBiometric(hasCredentials);
              
              // If credentials exist, automatically trigger biometric login
              // Don't show the button - just auto-login
              if (hasCredentials) {
                console.log('[App] Biometric credentials found, attempting automatic login...');
                // Small delay to let the UI render first
                setTimeout(() => {
                  handleBiometricLogin();
                }, 300);
              } else {
                // No credentials, don't show button (user hasn't set it up yet)
                setShowBiometricButton(false);
                console.log('[App] No biometric credentials stored yet - user can set up after login');
              }
            } catch (error) {
              // KeychainError when checking credentials is normal if they don't exist
              if (error.message?.includes('KeychainError') || error.errorMessage?.includes('KeychainError')) {
                console.log('[App] No biometric credentials stored (this is normal for first-time users)');
                setShowBiometricButton(false);
              } else {
                console.warn('[App] Error checking biometric credentials:', error);
              }
            }
          }
        } catch (error) {
          // Silently handle errors - biometric is optional
          console.warn('[App] Could not check biometric availability on mount:', error);
          setBiometricAvailable(false);
        }
      }
    };
    
    // Add a small delay to ensure Capacitor is fully initialized
    setTimeout(() => {
      checkBiometric();
    }, 500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // Function to complete login with selected profile
  const completeLogin = async (profile, formattedPhone, password, nextPath = '/team-selection') => {
    try {
      const formData = new URLSearchParams();
      formData.append('phone', formattedPhone);
      formData.append('password', password);
      formData.append('user_type', profile.user_type);
      
      // Detect login method: app_ios, app_android, or browser
      let loginMethod = 'browser';
      if (window.Capacitor?.isNativePlatform()) {
        const platform = window.Capacitor.getPlatform();
        if (platform === 'ios') {
          loginMethod = 'app_ios';
        } else if (platform === 'android') {
          loginMethod = 'app_android';
        }
      }
      formData.append('login_method', loginMethod);
      
      const response = await api.post('check_login.php', formData, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      });

      const result = response.data;
      if (result.success) {
        clearSessionState();
        authService.setTokens(result.access_token, result.refresh_token);
        applyIdentityFromLoginResult(result, { phone: formattedPhone });
        
        // Helper function to check and update device token
        const checkAndUpdateDeviceToken = async (token = null) => {
          if (!isCapacitor) {
            console.log('[App] Not Capacitor, skipping device token check');
            return;
          }
          
          try {
            console.log('[App] Checking device token status...');
            console.log('[App] Phone number:', formattedPhone);
            console.log('[App] Token available:', !!token);
            
            // Check if notifications are already granted
            const notificationGranted = await pushNotificationService.checkPermissionStatus();
            console.log('[App] Notification permission granted:', notificationGranted);
            
            // Even if notifications aren't granted yet, we should still check the database
            // in case a token was previously registered
            if (!notificationGranted) {
              console.log('[App] Notifications not granted yet, but will still check database for existing token');
            }
            
            // Get token if not provided
            if (!token) {
              token = pushNotificationService.getCurrentToken();
              console.log('[App] Retrieved token from service:', !!token);
            }
            
            if (!token) {
              console.log('[App] No token available yet, will check database anyway');
            }
            
            // Always check the database - even without a token, we want to see if one exists
            // Get access token from localStorage (where authService stores it)
            const accessToken = authService.getAccessToken();
            const headers = {
              'Content-Type': 'application/json'
            };
            
            if (accessToken) {
              headers['Authorization'] = `Bearer ${accessToken}`;
              console.log('[App] Access token found, adding to Authorization header');
            } else {
              console.warn('[App] ⚠️ No access token found - request may fail if endpoint requires auth');
            }
            
            // Collect user roles for user_type
            const userRoles = [];
            if (sessionStorage.getItem('player_id')) {
              userRoles.push('player');
            }
            if (sessionStorage.getItem('is_parent') === 'true' || result.user_type === 'parent') {
              userRoles.push('parent');
            }
            if (sessionStorage.getItem('is_manager') === 'true' || result.user_type === 'manager' || result.user_type === 'coach') {
              userRoles.push('coach');
            }
            const userTypeString = userRoles.length > 0 ? userRoles.join(',') : 'player';
            
            const requestBody = {
              phone: formattedPhone
            };
            
            // Only include token data if we have a token
            if (token) {
              requestBody.device_token = token;
              requestBody.device_type = Capacitor.getPlatform();
              requestBody.user_type = userTypeString;
            }
            
            const apiUrl = `${process.env.REACT_APP_API_URL}/check_device_token.php`;
            console.log('[App] Calling check_device_token.php with:', {
              phone: formattedPhone,
              hasToken: !!token,
              device_type: token ? Capacitor.getPlatform() : 'N/A',
              user_type: userTypeString,
              apiUrl: apiUrl
            });
            console.log('[App] Request body:', JSON.stringify(requestBody));
            console.log('[App] Request headers:', headers);
            
            if (!process.env.REACT_APP_API_URL) {
              console.error('[App] ❌ REACT_APP_API_URL is not defined!');
              return;
            }
            
            const checkResponse = await fetch(apiUrl, {
              method: 'POST',
              headers: headers,
              body: JSON.stringify(requestBody)
            });
            
            console.log('[App] check_device_token.php response status:', checkResponse.status);
            console.log('[App] check_device_token.php response URL:', checkResponse.url);
            
            if (checkResponse.ok) {
              const checkResult = await checkResponse.json();
              console.log('[App] check_device_token.php response:', checkResult);
              
              if (checkResult.success) {
                if (checkResult.inserted) {
                  console.log('[App] ✅ Device token inserted into database');
                } else if (checkResult.updated) {
                  console.log('[App] ✅ Device token updated in database');
                } else if (checkResult.exists) {
                  console.log('[App] ✅ Device token already exists in database');
                } else {
                  console.log('[App] ℹ️ No device token found in database (will be registered when token arrives)');
                }
              }
            } else {
              const errorText = await checkResponse.text();
              console.error('[App] ❌ check_device_token.php returned error:', checkResponse.status, errorText);
            }
          } catch (error) {
            console.error('[App] ❌ Error checking/updating device token:', error);
            console.error('[App] Error stack:', error.stack);
          }
        };
        
        // Check device token immediately after login (may not have token yet)
        // Use a small delay to ensure tokens are fully stored
        if (isCapacitor) {
          // Small delay to ensure tokens are stored in localStorage
          setTimeout(async () => {
            await checkAndUpdateDeviceToken();
          }, 100);
        }

        
        // Clarity tracking disabled - removed for App Store compliance
        // clarityService.captureUserData();
        // clarityService.setUserId(result.user_id);

        // Initialize push notifications after successful login
        if (isCapacitor) {
          console.log('[App] User logged in, initializing push notifications...');
          console.log('[App] SessionStorage user_id:', sessionStorage.getItem('user_id'));
          console.log('[App] SessionStorage player_id:', sessionStorage.getItem('player_id'));
          
          // Set up listeners for push notifications
          console.log('[App] Setting up push notification listeners after login...');
          pushNotificationService.addListeners({
            onRegistration: async (token) => {
              console.log('[App] ✅ Token received after login via listener:', token);
              // Wait a moment for saveTokenToServer to complete
              await new Promise(resolve => setTimeout(resolve, 500));
              // Call check_device_token.php when token is received
              console.log('[App] Calling check_device_token.php with token from listener...');
              await checkAndUpdateDeviceToken(token);
            },
            onRegistrationError: (error) => {
              console.error('[App] Registration error after login:', error);
            },
            onPushReceived: (notification) => {
              console.log('[App] Push received:', notification);
            },
            onPushActionPerformed: (notification) => {
              console.log('[App] Push action performed:', notification);
              handlePushNotificationAction(notification, navigate);
            }
          });
          
          // Initialize push notifications (this will request permission and get token)
          console.log('[App] Initializing push notifications after login...');
          await pushNotificationService.initialize();
          
          // Check for token after a short delay (iOS sometimes takes time)
          setTimeout(async () => {
            // First, manually check for token that might already be in window.fcmToken
            const manualToken = await pushNotificationService.manuallyCheckForToken();
            
            // Get token from multiple sources
            let token = pushNotificationService.getCurrentToken();
            if (!token) {
              token = manualToken;
            }
            if (!token && window.fcmToken) {
              token = window.fcmToken;
              console.log('[App] ✅ Found token in window.fcmToken');
            }
            if (!token) {
              const storedToken = localStorage.getItem('fcm_token');
              if (storedToken) {
                token = storedToken;
                console.log('[App] ✅ Found token in localStorage');
              }
            }
            
            if (token) {
              console.log('[App] ✅ Token found after login:', token.substring(0, 30) + '...');
              console.log('[App] Token length:', token.length);
              
              // Ensure token is stored in the service
              if (!pushNotificationService.currentToken) {
                pushNotificationService.currentToken = token;
                console.log('[App] ✅ Token stored in pushNotificationService.currentToken');
              }
              
              // Try to register any pending token now that user is logged in
              await pushNotificationService.registerPendingToken();
              
              // Wait a moment for saveTokenToServer to complete
              await new Promise(resolve => setTimeout(resolve, 1000));
              
              // Also call check_device_token.php to ensure it's in the database
              console.log('[App] Calling check_device_token.php with received token...');
              await checkAndUpdateDeviceToken(token);
              
              // Verify token is in database after a short delay
              setTimeout(async () => {
                console.log('[App] Verifying device token is in database...');
                const verified = await pushNotificationService.verifyAndRetryToken();
                if (!verified) {
                  console.warn('[App] ⚠️ Device token verification failed. User may not receive notifications.');
                }
              }, 2000);
            } else {
              console.warn('[App] No token received after 3 seconds. Check Xcode console for [AppDelegate] logs.');
              console.warn('[App] If you see a token in Xcode console, you can manually set it:');
              console.warn('[App] pushNotificationService.setTestToken("your-token-from-xcode-console")');
              
              // Still try to register pending token in case it exists
              await pushNotificationService.registerPendingToken();
              
              // Check again after a bit more time in case token arrives late
              setTimeout(async () => {
                // Check manually again
                const lateManualToken = await pushNotificationService.manuallyCheckForToken();
                const lateToken = pushNotificationService.getCurrentToken() || lateManualToken;
                
                if (lateToken) {
                  console.log('[App] ✅ Token received late, calling check_device_token.php...');
                  await checkAndUpdateDeviceToken(lateToken);
                } else {
                  console.log('[App] Still no token, checking database for existing token...');
                  await checkAndUpdateDeviceToken();
                }
              }, 2000);
            }
          }, 3000);
        }

        // After successful login, check if we should prompt for notifications and biometric
        // Do this AFTER push notifications but BEFORE navigation
        // Available for all user types: player, parent, manager, coach
        let shouldShowPrompt = false;
        let needsNotifications = false;
        let needsBiometric = false;

        if (window.Capacitor?.isNativePlatform()) {
          // Check notification permission status
          try {
            const receiveStatus = await pushNotificationService.getReceiveStatus();
            const notificationGranted = receiveStatus === 'granted';
            needsNotifications = !notificationGranted && shouldShowPeriodicPushModal();
            setNotificationDenied(receiveStatus === 'denied');
            console.log('[App] Notification permission status:', {
              status: receiveStatus,
              granted: notificationGranted,
              needsPrompt: needsNotifications
            });
          } catch (error) {
            console.error('[App] Error checking notification permission:', error);
          }

          // Check biometric availability and status
          let isBiometricAvailable = biometricAvailable;
          if (!isBiometricAvailable) {
            try {
              isBiometricAvailable = await biometricService.isAvailable();
              if (isBiometricAvailable) {
                setBiometricAvailable(true);
              }
            } catch (error) {
              console.error('[App] Error checking biometric availability:', error);
            }
          }

          if (isBiometricAvailable) {
            const biometricEnabled = authService.isBiometricEnabled();
            const hasStoredCredentials = await biometricService.hasStoredCredentials();
            needsBiometric = !biometricEnabled && !hasStoredCredentials;
            
            console.log('[App] Biometric status:', {
              biometricEnabled,
              hasStoredCredentials,
              needsPrompt: needsBiometric
            });
          }

          // Show combined prompt if either permission is needed
          if (needsNotifications || needsBiometric) {
            shouldShowPrompt = true;
            setNeedsNotificationPermission(needsNotifications);
            setNeedsBiometricPermission(needsBiometric);
            setShowPermissionsPrompt(true);
            if (needsNotifications) {
              markPushPromptShownThisSession();
            }
            console.log('[App] Showing permissions prompt after login:', {
              notifications: needsNotifications,
              biometric: needsBiometric,
              userType: result.user_type
            });
          }
        }

        // Only navigate if we're not showing the permissions prompt
        // If we are showing it, navigation will happen in the prompt handlers
        if (!shouldShowPrompt) {
          navigate(nextPath || '/team-selection');
        }
      } else {
        console.error('Login failed:', result.message);
        setError(result.message || 'Login failed');
      }
    } catch (error) {
      console.error('Login completion error:', error);
      setError('Failed to complete login. Please try again.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    let formattedPhone = phone.replace(/[^0-9]/g, '');
    if (formattedPhone.startsWith('0')) {
      formattedPhone = formattedPhone.substring(1);
    }

    try {
      // First, check for all matching profiles
      const formData = new URLSearchParams();
      formData.append('phone', formattedPhone);
      formData.append('password', password);
      
      const profileResponse = await api.post('check_login_profiles.php', formData, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      });

      const profileResult = profileResponse.data;
      
      if (!profileResult.success) {
        setError(profileResult.message || 'Invalid credentials');
        return;
      }

      // If single profile, proceed directly to login
      if (profileResult.single_profile) {
        await completeLogin(profileResult.profile, formattedPhone, password);
      } else {
        // Multiple profiles - show selection screen
        setAvailableProfiles(profileResult.profiles);
        setLoginAcademies(profileResult.academies || []);
        setShowProfileSelection(true);
      }
    } catch (error) {
      // Enhanced error logging for iOS network issues
      const errorDetails = {
        message: error.message,
        code: error.code,
        response: error.response?.data,
        status: error.response?.status,
        config: {
          url: error.config?.url,
          baseURL: error.config?.baseURL,
          method: error.config?.method
        },
        apiUrl: process.env.REACT_APP_API_URL,
        isCapacitor: window.Capacitor?.isNativePlatform() || false
      };
      
      console.error('Login error:', errorDetails);
      
      // Determine error message based on error type
      let errorMessage = 'An error occurred while trying to log in';
      
      if (error.code === 'ECONNABORTED') {
        errorMessage = 'Request timed out. Please check your internet connection.';
      } else if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
        errorMessage = `Network Error: Unable to connect to server. API URL: ${process.env.REACT_APP_API_URL || 'Not configured'}`;
      } else if (error.response?.status === 401) {
        errorMessage = 'Authentication required. The API endpoint may be misconfigured.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
    }
  };

  // Handle profile selection
  const handleProfileSelect = async (profile) => {
    setSelectedProfile(profile);
    setShowProfileSelection(false);
    
    let formattedPhone = phone.replace(/[^0-9]/g, '');
    if (formattedPhone.startsWith('0')) {
      formattedPhone = formattedPhone.substring(1);
    }
    
    await completeLogin(profile, formattedPhone, password);
  };

  const handleAcademyLogin = async (academyId) => {
    setShowProfileSelection(false);
    let formattedPhone = phone.replace(/[^0-9]/g, '');
    if (formattedPhone.startsWith('0')) {
      formattedPhone = formattedPhone.substring(1);
    }
    const profile = availableProfiles.find((row) => row.user_type === 'coach' || row.user_type === 'manager')
      || availableProfiles.find((row) => row.user_type === 'player')
      || availableProfiles[0];
    if (!profile) {
      setError('Could not open Academy Settings. Choose a profile first.');
      setShowProfileSelection(true);
      return;
    }
    await completeLogin(profile, formattedPhone, password, `/academy-subscriptions/${academyId}`);
  };


  // Handle enabling notifications from permissions prompt
  const handleEnableNotifications = async () => {
    try {
      console.log('[App] Enabling notifications...');
      const result = await pushNotificationService.initialize(true);
      
      console.log('[App] Initialize result:', result);
      
      // Check the result status
      if (result && result.status === 'granted') {
        clearPushReminderState();
        setNeedsNotificationPermission(false);
        setNotificationDenied(false);
        console.log('[App] Notifications enabled successfully');
        
        // If no other permissions needed, close prompt and navigate
        if (!needsBiometricPermission) {
          setShowPermissionsPrompt(false);
          navigate('/team-selection');
        }
      } else if (result && result.status === 'denied') {
        console.log('[App] Notification permission was denied - requires Settings');
        console.log('[App] Setting notificationDenied to true');
        // Update state to show denied message
        setNotificationDenied(true);
        // Ensure modal stays open and shows notification prompt
        setShowPermissionsPrompt(true);
        setNeedsNotificationPermission(true);
        // Keep the prompt open to show the important message
      } else {
        // Check current status as fallback
        console.log('[App] Result status unclear, checking permission status...');
        const granted = await pushNotificationService.checkPermissionStatus();
        console.log('[App] Permission status check result:', granted);
        
        if (granted) {
          clearPushReminderState();
          setNeedsNotificationPermission(false);
          setNotificationDenied(false);
          console.log('[App] Notifications enabled successfully');
          
          if (!needsBiometricPermission) {
            setShowPermissionsPrompt(false);
            navigate('/team-selection');
          }
        } else {
          console.log('[App] Notification permission was denied (fallback check)');
          setNotificationDenied(true);
          setShowPermissionsPrompt(true);
          setNeedsNotificationPermission(true);
        }
      }
    } catch (error) {
      console.error('[App] Error enabling notifications:', error);
      // On error, assume denied and show the message
      setNotificationDenied(true);
      setShowPermissionsPrompt(true);
      setNeedsNotificationPermission(true);
    }
  };
  
  // Handle opening Settings (for when permission is denied)
  const handleOpenSettings = async () => {
    await pushNotificationService.openSystemNotificationSettings();
  };

  const handleCheckAgain = async () => {
    try {
      const recovered = await pushNotificationService.recoverIfGranted();
      if (recovered) {
        clearPushReminderState();
        setNotificationDenied(false);
        setNeedsNotificationPermission(false);
        if (!needsBiometricPermission) {
          setShowPermissionsPrompt(false);
          navigate('/team-selection');
        }
        return;
      }
      setNotificationDenied(true);
      alert('Notifications are still off. Open Settings, turn on Allow Notifications, then try again.');
    } catch (error) {
      console.error('[App] Error checking notification permission:', error);
      alert('An error occurred while checking notification status. Please try again.');
    }
  };

  // Handle enabling biometric login from permissions prompt
  const handleEnableBiometricFromPrompt = async () => {
    try {
      // Get phone and password from sessionStorage (stored during login)
      const storedPhone = sessionStorage.getItem('phone');
      if (!storedPhone) {
        throw new Error('Phone number not found');
      }

      let formattedPhone = storedPhone.replace(/[^0-9]/g, '');
      if (formattedPhone.startsWith('0')) {
        formattedPhone = formattedPhone.substring(1);
      }

      // Get password from state (should be available from login)
      if (!password) {
        throw new Error('Password not available');
      }

      await authService.enableBiometricLogin(formattedPhone, password);
      setNeedsBiometricPermission(false);
      console.log('[App] Biometric login enabled successfully');
      
      // If no other permissions needed, close prompt and navigate
      if (!needsNotificationPermission) {
        setShowPermissionsPrompt(false);
        navigate('/team-selection');
      }
    } catch (error) {
      console.error('[App] Error enabling biometric login:', error);
      setError('Failed to enable biometric login. Please try again.');
      // Keep the prompt open so user can try again or skip
    }
  };

  // Handle enabling biometric login (legacy - from old modal)
  const handleEnableBiometric = async () => {
    try {
      let formattedPhone = phone.replace(/[^0-9]/g, '');
      if (formattedPhone.startsWith('0')) {
        formattedPhone = formattedPhone.substring(1);
      }

      await authService.enableBiometricLogin(formattedPhone, password);
      setShowBiometricPrompt(false);
      // Show success message (optional)
      console.log('Biometric login enabled successfully');
      
      // Navigate to team selection after enabling biometric
      navigate('/team-selection');
    } catch (error) {
      console.error('Error enabling biometric login:', error);
      setError('Failed to enable biometric login. Please try again.');
      // Still navigate even if enabling failed
      setShowBiometricPrompt(false);
      navigate('/team-selection');
    }
  };

  // Handle skipping permissions prompt
  const handleSkipPermissions = () => {
    if (needsNotificationPermission) {
      markPushPromptSkipped();
    }
    setShowPermissionsPrompt(false);
    setNeedsNotificationPermission(false);
    setNeedsBiometricPermission(false);
    navigate('/team-selection');
  };


  // Handle skipping biometric setup (legacy)
  const handleSkipBiometric = () => {
    setShowBiometricPrompt(false);
    // Store that user has seen the prompt (optional - to not show it again)
    // localStorage.setItem('biometric_prompt_seen', 'true');
    
    // Navigate to team selection after skipping
    navigate('/team-selection');
  };

  const renderLoginForm = () => {
    // All user types can now log in
    // Removed "Coming Soon" message for parent logins

    // Show login form for all user types (players, coaches, and parents)
    // Note: If biometric credentials exist, we auto-trigger login, so no button is shown
    return (
      <>
        {error && <div className="error-message">{error}</div>}
        
        <input
          type="tel"
          placeholder="Phone Number"
          className="input-field"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          autoComplete="username"
        />
        <input
          type="password"
          placeholder="Password"
          className="input-field"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
        />
        <button type="submit" className="submit-button">Sign In</button>
        <button 
          type="button" 
          className="register-button"
          onClick={() => setShowRegisterModal(true)}
        >
          Join
        </button>
      </>
    );
  };

  return (
    <div 
      className="App" 
      style={teamBackground ? { backgroundImage: `url(${teamBackground})` } : {}}
    >
      <div className="overlay"></div>
      {!showProfileSelection && (
        <header className="App-header">
          <div className="logo-container">
            {teamLogo ? (
              <div className="partnership-logos">
                <img 
                  src={teamLogo} 
                  alt="Team Logo" 
                  className="team-logo" 
                  onError={(e) => {
                    // If team logo fails to load, hide the partnership container and show single logo
                    e.target.parentElement.style.display = 'none';
                    e.target.parentElement.nextSibling.style.display = 'block';
                  }}
                />
                <div className="logo-separator">+</div>
                <img src={logo} alt="FirstWhistle Logo" className="firstwhistle-logo" />
              </div>
            ) : null}
            <img src={logo} alt="Logo" className="logo" style={{ display: teamLogo ? 'none' : 'block' }} />
          </div>
          
          {!isCapacitor && !showLoginForm ? (
            // Show app store buttons first on web
            <div className="app-store-buttons-container">
              <h2 className="download-app-title">Download the App</h2>
              <div className="app-store-buttons">
                <a 
                  href="https://apps.apple.com/gb/app/first-whistle-football/id6755308177?utm_source=web&utm_medium=login_page&utm_campaign=app_download"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="app-store-button"
                  data-track="app_store_download"
                  data-platform="ios"
                  onClick={() => {
                    // Track Apple App Store click
                    if (window.gtag) {
                      window.gtag('event', 'app_store_click', {
                        'store': 'apple',
                        'source': 'login_page'
                      });
                    }
                    // Fallback: log to console for debugging
                    console.log('App Store download clicked - iOS');
                  }}
                >
                  <img 
                    src="https://football.pebbledesigns.co.uk/api/images/btn-app-store.png" 
                    alt="Download on the App Store"
                  />
                </a>
                <a 
                  href="https://play.google.com/store/apps/details?id=app.firstwhistle.my&hl=en_GB&utm_source=web&utm_medium=login_page&utm_campaign=app_download"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="app-store-button"
                  data-track="play_store_download"
                  data-platform="android"
                  onClick={() => {
                    // Track Google Play Store click
                    if (window.gtag) {
                      window.gtag('event', 'app_store_click', {
                        'store': 'google_play',
                        'source': 'login_page'
                      });
                    }
                    // Fallback: log to console for debugging
                    console.log('App Store download clicked - Android');
                  }}
                >
                  <img 
                    src="https://football.pebbledesigns.co.uk/api/images/btn-play-store.png" 
                    alt="Get it on Google Play"
                  />
                </a>
              </div>
              <p className="or-continue-text">Or, continue to login</p>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  setShowLoginForm(true);
                }}
                className="login-link-button"
              >
                Log In
                <FaChevronRight className="chevron-icon" />
              </button>
              <p className="browser-login-warning">
                1st February 2026 the browser login will be switched off. Please download the app to keep all features.
              </p>
            </div>
          ) : (
            // Show login form (for mobile app or after clicking "Log In" on web)
            <>
              <form className="login-form" onSubmit={handleSubmit} autoComplete="off">
                {renderLoginForm()}
              </form>
              <div className="link-container">
                <a onClick={() => navigate('/forgot-password')} className="link-button">Forgot Password?</a>
                {!isCapacitor && (
                  <a 
                    onClick={(e) => {
                      e.preventDefault();
                      setShowLoginForm(false);
                    }}
                    className="link-button"
                  >
                    Back to App Stores
                  </a>
                )}
                {/* <a onClick={() => window.open('https://firstwhistle.app/', '_blank')} className="link-button">Register my team</a> */}
              </div>
            </>
          )}
        </header>
      )}
      
      <Routes>
        {/* ... existing routes ... */}
      </Routes>

      {/* Profile Selection Modal */}
      {showProfileSelection && (
        <ProfileSelection 
          profiles={availableProfiles}
          onSelectProfile={handleProfileSelect}
          onClose={() => setShowProfileSelection(false)}
          extraActions={loginAcademies.filter(canOpenAcademySettings).map((academy) => ({
            id: `academy-${academy.id}`,
            title: 'Academy Settings',
            subtitle: academy.name,
            onClick: () => handleAcademyLogin(academy.id),
          }))}
        />
      )}

      {/* Biometric Prompt Modal (Legacy - kept for backward compatibility) */}
      {showBiometricPrompt && (
        <BiometricPromptModal
          onEnable={handleEnableBiometric}
          onSkip={handleSkipBiometric}
          onClose={handleSkipBiometric}
        />
      )}

      {/* Combined Permissions Prompt Modal */}
      {showPermissionsPrompt && (
        <PermissionsPromptModal
          showNotificationPrompt={needsNotificationPermission}
          showBiometricPrompt={needsBiometricPermission}
          notificationDenied={notificationDenied}
          howToSteps={getHowToEnableSteps()}
          variant="login"
          onEnableNotifications={handleEnableNotifications}
          onEnableBiometric={handleEnableBiometricFromPrompt}
          onOpenSettings={handleOpenSettings}
          onCheckAgain={handleCheckAgain}
          onSkip={handleSkipPermissions}
          onClose={handleSkipPermissions}
        />
      )}

      {/* Register Modal */}
      {showRegisterModal && (
        <NewRegisterModal
          onClose={() => {
            setShowRegisterModal(false);
            // Remove join parameter from URL when closing modal
            const searchParams = new URLSearchParams(location.search);
            if (searchParams.has('join')) {
              searchParams.delete('join');
              const newSearch = searchParams.toString();
              const newUrl = newSearch 
                ? `${location.pathname}?${newSearch}${location.hash || ''}`
                : `${location.pathname}${location.hash || ''}`;
              navigate(newUrl, { replace: true });
            } else if (location.hash === '#join') {
              // Remove hash if it's just #join
              navigate(location.pathname + location.search, { replace: true });
            }
          }}
        />
      )}
    </div>
  );
}

export default App;
